import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '../../i18n/format'
import {
  ANDROID_BANNER_DISMISSED_KEY,
  APP_ONELINK_URL,
  getMobilePlatform,
  isAppPromoPath,
  PLAY_STORE_RATING,
  readFlag,
  trackAppPromo,
  writeFlag,
} from '../../utils/appPromo'
import './appPromo.css'

// The app's name, the same in every language.
const APP_NAME = 'maayot'

const STAR_PATH =
  'M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95L12 2.5z'

const Star: React.FC<{ fill: number }> = ({ fill }) => (
  <span className='app-promo-banner__star'>
    <svg width='11' height='11' viewBox='0 0 24 24' aria-hidden='true'>
      <path d={STAR_PATH} fill='currentColor' />
    </svg>
    <span className='app-promo-banner__star-fill' style={{ width: `${fill * 100}%` }}>
      <svg width='11' height='11' viewBox='0 0 24 24' aria-hidden='true'>
        <path d={STAR_PATH} fill='currentColor' />
      </svg>
    </span>
  </span>
)

/** Five stars rounded to the nearest half, as the stores draw them (4.3 shows as 4½, 4.9 as 5). */
const RatingStars: React.FC<{ rating: number }> = ({ rating }) => {
  const { t } = useTranslation('dashboard')
  const stars = Math.round(rating * 2) / 2
  return (
    <span className='app-promo-banner__stars' role='img' aria-label={t('appPromo.rated', { rating: String(rating) })}>
      {[0, 1, 2, 3, 4].map((index) => (
        <Star key={index} fill={Math.min(Math.max(stars - index, 0), 1)} />
      ))}
    </span>
  )
}

// Above this width the header is position: fixed and would sit on top of the banner.
const MAX_BANNER_WIDTH = 991

/**
 * Android stand-in for Safari's Smart App Banner (the `apple-itunes-app` meta tag in
 * public/index.html), which Chrome has no equivalent of. Sits in the page flow above the
 * header, which is static at phone widths, and stays until the learner closes it or taps Get.
 */
const AndroidAppBanner: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const { pathname } = useLocation()
  const [dismissed, setDismissed] = useState(() => readFlag(ANDROID_BANNER_DISMISSED_KEY))

  const visible =
    !dismissed &&
    isAppPromoPath(pathname) &&
    Boolean(localStorage.getItem('authToken')) &&
    getMobilePlatform() === 'android' &&
    window.innerWidth <= MAX_BANNER_WIDTH

  // Once per page load, not once per route change.
  const trackedShown = useRef(false)
  useEffect(() => {
    if (!visible || trackedShown.current) return
    trackedShown.current = true
    trackAppPromo('app_promo_shown', 'android_banner')
  }, [visible])

  if (!visible) return null

  const dismiss = () => {
    writeFlag(ANDROID_BANNER_DISMISSED_KEY)
    setDismissed(true)
  }

  return (
    <div className='app-promo-banner'>
      <button
        type='button'
        className='app-promo-banner__close'
        aria-label={t('appPromo.dismiss')}
        onClick={() => {
          dismiss()
          trackAppPromo('app_promo_dismissed', 'android_banner')
        }}
      >
        <svg width='12' height='12' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
          <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' />
        </svg>
      </button>
      <img className='app-promo-banner__icon' src='/images/app-promo/app-icon.png' alt='' width='44' height='44' />
      <span className='app-promo-banner__body'>
        <strong className='app-promo-banner__title'>{APP_NAME}</strong>
        <span className='app-promo-banner__rating'>
          <RatingStars rating={PLAY_STORE_RATING} />
          <span>{formatNumber(PLAY_STORE_RATING, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
        </span>
        <span className='app-promo-banner__text'>{t('appPromo.freeOnPlay')}</span>
      </span>
      <a
        className='app-promo-banner__get'
        href={APP_ONELINK_URL}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => {
          dismiss()
          trackAppPromo('app_promo_install_clicked', 'android_banner')
        }}
      >
        {t('appPromo.get')}
      </a>
    </div>
  )
}

export default AndroidAppBanner
